import {Chess} from "chess.js";
import React, {useEffect, useState} from "react";
import Chessboard from "chessboardjsx";
import {calculateTakenPiecesFromFen} from "../utils/chessFunctions";
import {customPieceRenderings} from "../CustomPieces";
import {ChessPieces} from "../utils/chessPieces";

// Custom hook to track window width for dynamic board scaling
function useWindowWidth() {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return width;
}



const baseBoardWidth = 700; // baseline board width in px

//TODO We should move the size calculations out of this component - cause right now we have to hardcode everything
//around the board to get the sizes to match
const ChessBoard = (props) => {

    const PromotionModal = ({sourceSquare, targetSquare, piece}) => {
        const color = piece.includes("w") ? "white" : "black"

        const makePromotionMove = (sourceSquare, targetSquare, promotionFen) => {
            makeMove({sourceSquare, targetSquare, promotionFen})
            setPromotionInfo(null)
        }

        return (
            <div className="modal-overlay">
                <div className="modal-content" style={{width: scaledBoardWidth / 6, height: scaledBoardWidth / 2}}>
                    {Object.values(ChessPieces).filter((p) => p.fen !== "k" && p.fen !== "p").map(p => {
                        return <img src={p.getImageSrc(color)} alt={"A small picture of a chess piece"} className="promotion-piece"
                                    style={{width: "90%", height: "25%", cursor: "pointer",
                                        borderRadius: "0.2rem"}}
                                    onClick={() => makePromotionMove(sourceSquare, targetSquare, p.fen)}/>
                    })}
                </div>
            </div>
        )
    }

    const windowWidth = useWindowWidth();
    // Compute scale factor relative to a 1920px baseline
    const scaleFactor = windowWidth / 1920;
    // Compute the scaled board width (in px) to pass to Chessboard
    const scaledBoardWidth = baseBoardWidth * scaleFactor;

    // New state for click-to-move functionality
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [availableMoves, setAvailableMoves] = useState([]);
    const [promotionInfo, setPromotionInfo] = useState(null)

    const positionCustomSymbol = (customSymbol) => {

        const squareWidth = scaledBoardWidth / 8;

        let file, rank;
        if(props.orientation === "white") {
            file = customSymbol.square.charCodeAt(0) - 'a'.charCodeAt(0);
            rank = 8 - parseInt(customSymbol.square[1])
        } else {
            file = 'h'.charCodeAt(0) - customSymbol.square.charCodeAt(0)
            rank = parseInt(customSymbol.square[1]) - 1
        }

        //Define offset here
        const customOffset = 0.65 * squareWidth;

        return <div style={{position: "absolute", left: `${file * squareWidth + customOffset}px`, top: `${rank * squareWidth}px`, zIndex: 10}}>
            {customSymbol.symbol(0.35 * squareWidth)}
        </div>
    }

    const isYourMove = (color) => {
        //If it's not a live game you can move all colors
        if(!props.isLiveGame) {
            return true;
        }

        console.log(`Props color: ${props.color}. Move color: ${color}`)
        return props.color === color;
    }



    const checkPromotion = (sourceSquare, targetSquare, piece) => {
        if((piece === "wP" && targetSquare.includes("8")) || (piece === "bP" && targetSquare.includes("1"))) {
            setPromotionInfo({sourceSquare, targetSquare, piece})
            return true;
        }

        return false;
    }

    const handleDropWithPromotion = ({sourceSquare, targetSquare, piece}) => {
        if(checkPromotion(sourceSquare, targetSquare, piece)) {
            return
        }

        makeMove({sourceSquare, targetSquare})
    }

    // onDrop uses the color ref to block moves when it isn’t the player’s turn
    const makeMove = ({sourceSquare, targetSquare, promotionFen = null}) => {
        const move = {
            from: sourceSquare,
            to: targetSquare,
            promotion: promotionFen
        };
        try {
            const chess = new Chess(props.fen);
            if (!isYourMove(chess.turn())) {
                console.error("Invalid move - not your turn");
                return;
            }
            const legal = chess.move(move);
            if (legal) {
                props.moveCallback(chess.fen(), move)
                setSelectedSquare(null);
                setAvailableMoves([]);
            } else {
                console.log("Invalid move");
            }
        } catch (err) {
            console.error("Invalid move:", err);
        }
    };

    const squareToPieceString = (chess, square) => {
        return chess.get(square).color + chess.get(square).type.toUpperCase()
    }

    // is used to handle click moves on chessboard
    const handleSquareClick = (square) => {
        const chess = new Chess(props.fen);
        if (selectedSquare) {
            if (selectedSquare === square) {
                setSelectedSquare(null);
                setAvailableMoves([]);
                return;
            }
            if (availableMoves.includes(square)) {
                if(checkPromotion(selectedSquare, square, squareToPieceString(chess, selectedSquare))) return;

                const move = {
                    from: selectedSquare,
                    to: square
                };
                const legal = chess.move(move);
                if (legal) {
                    props.moveCallback(chess.fen(), move)
                } else {
                    console.log("Invalid move");
                }
                setSelectedSquare(null);
                setAvailableMoves([]);
            } else {
                const piece = chess.get(square);
                if (piece && isYourMove(piece.color)) {
                    setSelectedSquare(square);
                    const moves = chess.moves({square, verbose: true});
                    setAvailableMoves(moves.map((m) => m.to));
                } else {
                    setSelectedSquare(null);
                    setAvailableMoves([]);
                }
            }
        } else {
            const piece = chess.get(square);
            if (piece && isYourMove(piece.color)) {
                setSelectedSquare(square);
                const moves = chess.moves({square, verbose: true});
                setAvailableMoves(moves.map((m) => m.to));
            }
        }
    };

    const squareStyles = {};
    if (selectedSquare) {
        squareStyles[selectedSquare] = {
            boxShadow: "inset 0 0 0 0.25rem rgba(98, 86, 206, 0.8)",
        };
    }
    availableMoves.forEach((sq) => {
        squareStyles[sq] = {
            boxShadow: "inset 0 0 0 0.25rem rgba(173,216,230,0.8)",
        };
    });

    const translateFenLetterToImage = (piece, color, styleProps) => {
        let imageSrc = piece.getImageSrc(color);

        return <img src={imageSrc} alt={`A ${imageSrc.split(".")[0]}`} style={{maxWidth: '2rem', maxHeight: '2rem', display: "block", ...styleProps}}/>
    }

    //We should probably get an enum soon - this easily breaks
    const getPointDiff = (fen, color) => {
        const lostPiecesWhite = calculateTakenPiecesFromFen(fen, "white");
        const lostPiecesBlack = calculateTakenPiecesFromFen(fen, "black")

        let whitePointsLost = 0;
        let blackPointsLost = 0
        lostPiecesWhite.forEach((amount, piece) => whitePointsLost += piece.value * amount)
        lostPiecesBlack.forEach((amount, piece) => blackPointsLost += piece.value * amount)

        //The color corresponds to the color of the pieces being TAKEN
        const pointDiff = whitePointsLost - blackPointsLost;
        if(color === "black") {
            return -1 * pointDiff;
        } else {
            return pointDiff;
        }
    }

    const getTakenPieces = (fen, color, style) => {
        const takenPieces = calculateTakenPiecesFromFen(fen, color);
        const pointDiff = getPointDiff(fen, color)
        return (<div style={{position: "absolute", display: "flex", height: "2rem", ...style}}>
            {[...takenPieces].map(([piece, amount]) => {
                let pieceImages = [];
                for(let i = 0; i < amount; i++) {
                    //This ensures that several of the same pieces are grouped closer together
                    const styleProps = i > 0 ? {marginLeft: "-1.25rem"} : {marginLeft: "0rem"}
                    pieceImages.push(translateFenLetterToImage(piece, color, styleProps))
                }

                return <div style={{display: "flex"}}>{pieceImages}</div>
            })}
            {pointDiff > 0 && <div style={{marginTop: "0.5rem"}}>+{pointDiff}</div>}
        </div>)
    }

    const topStyle = {left: "0", top: "-2.5rem"}
    const botStyle = {marginTop: "0.2rem"}
    return (<div style={{position: "relative"}}>
        {props.orientation === "white" ? getTakenPieces(props.fen, "white", topStyle) : getTakenPieces(props.fen, "black", topStyle)}
        <div className="board-container">
            {props.customSymbol && positionCustomSymbol(props.customSymbol)}
            <Chessboard
                position={props.fen}
                onDrop={handleDropWithPromotion}
                onSquareClick={handleSquareClick}
                squareStyles={squareStyles}
                darkSquareStyle={{backgroundColor: "#FFCCD5"}}
                lightSquareStyle={{backgroundColor: "#FFF5F8"}}
                orientation={props.orientation}
                width={scaledBoardWidth}
                pieces={customPieceRenderings}
            />
        </div>
        {promotionInfo && <PromotionModal sourceSquare={promotionInfo.sourceSquare} targetSquare={promotionInfo.targetSquare} piece={promotionInfo.piece}/>}
        {props.orientation === "white" ? getTakenPieces(props.fen, "black", botStyle) : getTakenPieces(props.fen, "white", botStyle)}
    </div>)
}

export default ChessBoard;